import { DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, Modal, RiskActionButton, Space, Typography } from '@wanjianshi/admin-ui';
import type { ApiResponse } from '@wanjianshi/shared-contracts';
import { useCallback, useEffect, useState } from 'react';
import { useAdminAuth } from '@/auth/store';
import { AdminRemoteSelect } from '@/components/AdminRemoteSelect';
import { adminApi, adminOperationOptions } from '@/services/api/client';

interface InviteeOption {
  id: string | number;
  nickname?: string | null;
  username?: string | null;
  mobile_masked?: string | null;
  current_inviter_id?: string | number | null;
  current_inviter?: InviteeOption | null;
}

interface InviterBindingModalProps {
  userId: string | number;
  userLabel: string;
  inviteeCount?: number;
  onSaved: () => Promise<void>;
}

function optionLabel(user: InviteeOption): string {
  const name = user.nickname || user.username || `用户 ${String(user.id)}`;
  return `${name} · ${user.mobile_masked || `ID ${String(user.id)}`}`;
}

export function InviterBindingModal({
  userId,
  userLabel,
  inviteeCount = 0,
  onSaved,
}: InviterBindingModalProps) {
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [invitees, setInvitees] = useState<InviteeOption[]>([]);
  const [selectedInvitee, setSelectedInvitee] = useState<InviteeOption | null>(null);
  const [form] = Form.useForm();
  const canBind = hasPermission('wjs-admin:user:bind-inviter');
  const canRebind = hasPermission('wjs-admin:user:rebind-inviter');
  const canClear = hasPermission('wjs-admin:user:clear-inviter');

  const loadInvitees = useCallback(async () => {
    const response = await adminApi.get<ApiResponse<{ items: InviteeOption[] }>>(
      `/admin/users/${userId}/invitees`,
      { params: { limit: 200 } },
    );
    setInvitees(response.data.items ?? []);
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    void loadInvitees().catch(() => message.error('被邀请人列表加载失败'));
  }, [loadInvitees, message, open]);

  const loadOptions = useCallback(async (keyword: string, signal: AbortSignal) => {
    const response = await adminApi.get<ApiResponse<{ items: InviteeOption[] }>>(
      `/admin/users/${userId}/invitee-options`,
      { params: { keyword, limit: 20 }, signal },
    );
    return response.data.items ?? [];
  }, [userId]);

  if (!canBind && !canRebind && !canClear) return null;

  const submit = async () => {
    let values: { invitee_id: string; reason: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const inviteeId = Number(values.invitee_id);
    const isRebind = Boolean(selectedInvitee?.current_inviter_id);
    if (selectedInvitee?.current_inviter_id && String(selectedInvitee.current_inviter_id) === String(userId)) {
      message.info('该账号已经在当前邀请人名下');
      return;
    }
    if (isRebind && !canRebind) {
      message.error('该账号已有邀请人，当前角色没有换绑权限');
      return;
    }
    if (!isRebind && !canBind) {
      message.error('当前角色没有绑定权限');
      return;
    }
    setSubmitting(true);
    try {
      const options = adminOperationOptions(values.reason.trim());
      if (isRebind) {
        await adminApi.put(`/admin/users/${userId}/invitees/${inviteeId}`, undefined, options);
      } else {
        await adminApi.post(`/admin/users/${userId}/invitees`, { invitee_id: inviteeId }, options);
      }
      message.success(isRebind ? '被邀请人换绑成功' : '被邀请人绑定成功');
      form.resetFields();
      setSelectedInvitee(null);
      await loadInvitees();
      await onSaved();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '被邀请人操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async (invitee: InviteeOption, reason: string) => {
    setSubmitting(true);
    try {
      await adminApi.delete(
        `/admin/users/${userId}/invitees/${invitee.id}`,
        adminOperationOptions(reason),
      );
      message.success('被邀请人已移除');
      await loadInvitees();
      await onSaved();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '被邀请人移除失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        icon={<LinkOutlined />}
        onClick={() => setOpen(true)}
      >
        管理被邀请人{inviteeCount ? `（${inviteeCount}）` : ''}
      </Button>
      <Modal
        open={open}
        title={`管理被邀请人 · ${userLabel}`}
        okText="确认绑定"
        cancelText="关闭"
        confirmLoading={submitting}
        onOk={() => void submit()}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          title={`当前邀请人：${userLabel}（用户 ID：${String(userId)}）`}
          description="在当前邀请人账号下绑定被邀请人；一个邀请人可以绑定多个被邀请人，一个被邀请人只能绑定一个邀请人。"
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="invitee_id"
            label="被邀请人"
            rules={[{ required: true, message: '请选择被邀请人' }]}
          >
            <AdminRemoteSelect<InviteeOption>
              loadOptions={loadOptions}
              mapOption={user => ({ value: String(user.id), label: optionLabel(user) })}
              onSelectRecord={record => setSelectedInvitee(record)}
              placeholder="按用户 ID、账号、昵称或手机号搜索"
            />
          </Form.Item>
          {selectedInvitee?.current_inviter_id ? (
            <Alert
              type={String(selectedInvitee.current_inviter_id) === String(userId) ? 'info' : 'warning'}
              showIcon
              title={String(selectedInvitee.current_inviter_id) === String(userId)
                ? '该账号已经在当前邀请人名下'
                : `该账号当前邀请人为 ${selectedInvitee.current_inviter ? optionLabel(selectedInvitee.current_inviter) : `用户 ${String(selectedInvitee.current_inviter_id)}`}，确认后将执行换绑`}
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <Form.Item name="reason" label="操作原因" rules={[{ required: true, message: '请输入操作原因' }]}>
            <Input.TextArea rows={3} placeholder="说明本次绑定或换绑原因，内容会进入操作审计" />
          </Form.Item>
        </Form>
        <Typography.Text type="secondary">当前已绑定 {invitees.length || inviteeCount} 人</Typography.Text>
        <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
          {invitees.map(invitee => (
            <Space key={String(invitee.id)} style={{ width: '100%', justifyContent: 'space-between' }}>
              <span>{optionLabel(invitee)}</span>
              {canClear ? (
                <RiskActionButton
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  allowed
                  title={`移除被邀请人 · ${optionLabel(invitee)}`}
                  description={`将解除 ${optionLabel(invitee)} 与 ${userLabel} 的邀请关系。`}
                  onConfirm={reason => clear(invitee, reason)}
                >
                  移除
                </RiskActionButton>
              ) : null}
            </Space>
          ))}
        </Space>
      </Modal>
    </>
  );
}
